import { Injectable, Signal } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { FormSaverComponent } from '@catch/general-ui';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PlatformChangesGuard {
  /**
   * Blocks navigation when the active view has unsaved FormSaver changes.
   * @param component - Routed component that may expose `formSaver`
   * @param _currentRoute - Current route snapshot
   * @param _currentState - Current router state
   * @param _nextState - Target router state
   * @returns Whether deactivation is allowed
   */
  canDeactivate(
    component: { formSaver?: FormSaverComponent | Signal<FormSaverComponent | undefined> },
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    _nextState?: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const formSaver = this.resolveFormSaver(component?.formSaver);
    if (formSaver?.hasChanges()) {
      formSaver.shake();
      return false;
    }
    return true;
  }

  /**
   * Resolves a FormSaver from either a component instance or a `viewChild()` signal.
   * @param formSaverRef - FormSaver ref from the routed component
   * @returns The FormSaver instance when available
   */
  private resolveFormSaver(
    formSaverRef:
      | FormSaverComponent
      | Signal<FormSaverComponent | undefined>
      | undefined
      | null
  ): FormSaverComponent | undefined {
    if (!formSaverRef) {
      return undefined;
    }
    if (typeof formSaverRef === 'function') {
      return (formSaverRef as Signal<FormSaverComponent | undefined>)();
    }
    return formSaverRef;
  }
}
